import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { mitigationDryingDehumidifierKindSchema, mitigationDryingMaterialTypeSchema, mitigationDryingSizingPolicySchema, mitigationDryingWaterClassSchema, } from '../contracts/mitigation-drying-workspace.contract.js';
export const mitigationDryingConfigurationValueSchema = z.strictObject({
    panelEnabled: z.boolean(),
    defaultPointLabel: z.string().trim().min(1).max(160),
    defaultWaterClass: mitigationDryingWaterClassSchema,
    defaultDehumidifierKind: mitigationDryingDehumidifierKindSchema,
    defaultMaterialDryGoalsMillis: z.record(mitigationDryingMaterialTypeSchema, z.number().int().min(0).max(999_000)),
    sizingPolicy: mitigationDryingSizingPolicySchema,
    maximumSitesPerProject: z.number().int().min(1).max(100),
    maximumChambersPerProject: z.number().int().min(1).max(500),
    maximumRoomsPerProject: z.number().int().min(1).max(2_000),
    maximumPointsPerProject: z.number().int().min(1).max(20_000),
});
export const mitigationDryingConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'mitigation.drying.configuration',
        moduleId: 'mitigation',
        title: 'Mitigation drying configuration',
        helpText: 'Controls bounded drying-workspace defaults and the disclosed initial equipment-sizing method.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelEnabled: true,
            defaultPointLabel: 'General',
            defaultWaterClass: 2,
            defaultDehumidifierKind: 'lgr',
            defaultMaterialDryGoalsMillis: {
                drywall: 16_000,
                hardwood: 12_000,
                concrete: 16_000,
                carpet: 16_000,
                plywood: 15_000,
                other: 16_000,
            },
            sizingPolicy: {
                floorSquareFeetPerAirMover: { '1': 70, '2': 50, '3': 40, '4': 40 },
                wallLinearFeetPerAirMover: 14,
                cubicFeetPerPint: {
                    refrigerant: { '1': 100, '2': 50, '3': 40, '4': 40 },
                    lgr: { '1': 100, '2': 40, '3': 30, '4': 30 },
                    desiccant: { '1': 100, '2': 40, '3': 30, '4': 30 },
                },
                dehumidifierCapacityPintsPerDay: 110,
            },
            maximumSitesPerProject: 20,
            maximumChambersPerProject: 100,
            maximumRoomsPerProject: 500,
            maximumPointsPerProject: 5_000,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: mitigationDryingConfigurationValueSchema,
});
export const mitigationDryingDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showArchived: z.boolean(),
    showSizingWorksheet: z.boolean(),
    readingHistoryLimit: z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)]),
});
export const mitigationDryingDisplaySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.drying.display',
        moduleId: 'mitigation',
        title: 'Mitigation drying display',
        helpText: 'Controls drying cards, density, archived records, sizing visibility, and bounded reading history.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            showArchived: false,
            showSizingWorksheet: true,
            readingHistoryLimit: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationDryingDisplayValueSchema,
});
const label = z.string().trim().min(1).max(160);
export const mitigationDryingCopyValueSchema = z.strictObject({
    panelTitle: label,
    panelDescription: z.string().trim().min(1).max(400),
    setupSectionTitle: label,
    readingsSectionTitle: label,
    sizingSectionTitle: label,
    siteLabel: label,
    chamberLabel: label,
    roomLabel: label,
    pointLabel: label,
    nameLabel: label,
    materialLabel: label,
    dryGoalLabel: label,
    affectedFloorLabel: label,
    ceilingHeightLabel: label,
    affectedWallLabel: label,
    affectedCeilingLabel: label,
    obstructionCountLabel: label,
    waterClassLabel: label,
    dehumidifierKindLabel: label,
    workDateLabel: label,
    readingTypeLabel: label,
    readingUnitLabel: label,
    readingValueLabel: label,
    occurredAtLabel: label,
    notesLabel: label,
    correctsReadingLabel: label,
    newReadingLabel: label,
    reasonLabel: label,
    addSiteAction: label,
    addChamberAction: label,
    addRoomAction: label,
    addPointAction: label,
    recordReadingAction: label,
    voidReadingAction: label,
    archiveAction: label,
    restoreAction: label,
    calculateSizingAction: label,
    recommendedAirMoversLabel: label,
    recommendedDehumidifiersLabel: label,
    requiredPintsLabel: label,
    fieldAdjustmentNotice: z.string().trim().min(1).max(400),
    loadingLabel: label,
    emptyWorkspaceLabel: z.string().trim().min(1).max(300),
    emptyReadingsLabel: z.string().trim().min(1).max(300),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    readingTypeLabels: z.record(z.enum(['mc', 'gpp', 'rh', 'temperature']), label),
    readingUnitLabels: z.record(z.enum(['percent', 'relative', 'grains-per-pound', 'fahrenheit']), label),
    pointKindLabels: z.record(z.enum(['material-moisture', 'affected-air', 'unaffected-air', 'exterior-air', 'equipment-air']), label),
    materialLabels: z.record(mitigationDryingMaterialTypeSchema, label),
    dehumidifierKindLabels: z.record(mitigationDryingDehumidifierKindSchema, label),
});
export const mitigationDryingCopySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.copy.drying',
        moduleId: 'mitigation',
        title: 'Mitigation drying interface copy',
        helpText: 'Controls tenant-editable drying workspace, monitoring, and sizing labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Drying workspace',
            panelDescription: 'Organize drying areas, monitoring points, retained readings, and an initial equipment-sizing worksheet.',
            setupSectionTitle: 'Drying setup',
            readingsSectionTitle: 'Monitoring readings',
            sizingSectionTitle: 'Initial equipment sizing',
            siteLabel: 'Site',
            chamberLabel: 'Chamber',
            roomLabel: 'Room',
            pointLabel: 'Monitoring point',
            nameLabel: 'Name',
            materialLabel: 'Material',
            dryGoalLabel: 'Dry goal',
            affectedFloorLabel: 'Affected floor area (sq ft)',
            ceilingHeightLabel: 'Ceiling height (ft)',
            affectedWallLabel: 'Affected wall length (ft)',
            affectedCeilingLabel: 'Affected ceiling area (sq ft)',
            obstructionCountLabel: 'Obstructions',
            waterClassLabel: 'Water class',
            dehumidifierKindLabel: 'Dehumidifier type',
            workDateLabel: 'Drying work date',
            readingTypeLabel: 'Reading type',
            readingUnitLabel: 'Unit',
            readingValueLabel: 'Reading value',
            occurredAtLabel: 'Occurred at',
            notesLabel: 'Notes',
            correctsReadingLabel: 'Corrects retained reading',
            newReadingLabel: 'New reading',
            reasonLabel: 'Change reason',
            addSiteAction: 'Add site',
            addChamberAction: 'Add chamber',
            addRoomAction: 'Add room',
            addPointAction: 'Add monitoring point',
            recordReadingAction: 'Record reading',
            voidReadingAction: 'Void reading',
            archiveAction: 'Archive',
            restoreAction: 'Restore',
            calculateSizingAction: 'Calculate starting point',
            recommendedAirMoversLabel: 'Recommended air movers',
            recommendedDehumidifiersLabel: 'Recommended dehumidifiers',
            requiredPintsLabel: 'Required pints per day',
            fieldAdjustmentNotice: 'This is a documented starting point. Field conditions and daily monitoring determine adjustments.',
            loadingLabel: 'Loading drying workspace…',
            emptyWorkspaceLabel: 'No drying workspace has been created for this Project.',
            emptyReadingsLabel: 'No monitoring readings have been retained for this Project.',
            loadError: 'The drying workspace could not be loaded.',
            mutationError: 'The drying change could not be saved. Refresh and try again.',
            readingTypeLabels: {
                mc: 'Material moisture',
                gpp: 'Grains per pound',
                rh: 'Relative humidity',
                temperature: 'Temperature',
            },
            readingUnitLabels: {
                percent: 'Percent',
                relative: 'Relative scale',
                'grains-per-pound': 'Grains per pound',
                fahrenheit: 'Degrees Fahrenheit',
            },
            pointKindLabels: {
                'material-moisture': 'Material moisture',
                'affected-air': 'Affected air',
                'unaffected-air': 'Unaffected air',
                'exterior-air': 'Exterior air',
                'equipment-air': 'Equipment air',
            },
            materialLabels: {
                drywall: 'Drywall',
                hardwood: 'Hardwood',
                concrete: 'Concrete',
                carpet: 'Carpet',
                plywood: 'Plywood',
                other: 'Other',
            },
            dehumidifierKindLabels: {
                refrigerant: 'Refrigerant',
                lgr: 'LGR',
                desiccant: 'Desiccant',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationDryingCopyValueSchema,
});
export const mitigationDryingSettingRegistrations = [
    mitigationDryingConfigurationSetting,
    mitigationDryingDisplaySetting,
    mitigationDryingCopySetting,
];
//# sourceMappingURL=mitigation-drying-setting-definitions.js.map